/**
 * Slash command extension for TipTap — Notion-style.
 * Type `/` in the editor to surface a floating menu of block options.
 *
 * Key implementation notes:
 *  • Menu items use `onMouseDown` (NOT onClick) with preventDefault so the
 *    editor never loses focus during selection — this keeps the suggestion
 *    plugin's tracked range valid until the chain runs.
 *  • Each item carries an `action(editor, range)` callback. The Suggestion
 *    config's outer command simply invokes it. Using TipTap's high-level
 *    command helpers (`setHeading`, `toggleBulletList`, …) is more robust
 *    than the lower-level `setNode("heading", …)` because they handle
 *    edge-cases like converting a wrapped list item back to a heading.
 */
import { Extension } from "@tiptap/core";
import Suggestion from "@tiptap/suggestion";
import { ReactRenderer } from "@tiptap/react";
import tippy from "tippy.js";
import {
  forwardRef, useEffect, useImperativeHandle, useState,
} from "react";
import {
  Heading2, Heading3, List, ListOrdered, CheckSquare, Quote, Code, Minus,
} from "lucide-react";

// --- Available slash commands (Notion-style) ---
const SLASH_ITEMS = [
  {
    title: "Heading 2",
    description: "Medium section heading",
    icon: Heading2,
    keywords: ["h2", "heading", "title"],
    action: (editor, range) =>
      editor.chain().focus().deleteRange(range).setHeading({ level: 2 }).run(),
  },
  {
    title: "Heading 3",
    description: "Small section heading",
    icon: Heading3,
    keywords: ["h3", "heading", "subtitle"],
    action: (editor, range) =>
      editor.chain().focus().deleteRange(range).setHeading({ level: 3 }).run(),
  },
  {
    title: "Bullet list",
    description: "Simple bulleted list",
    icon: List,
    keywords: ["bullet", "list", "ul", "unordered"],
    action: (editor, range) =>
      editor.chain().focus().deleteRange(range).toggleBulletList().run(),
  },
  {
    title: "Numbered list",
    description: "1. Ordered list",
    icon: ListOrdered,
    keywords: ["numbered", "ordered", "list", "ol"],
    action: (editor, range) =>
      editor.chain().focus().deleteRange(range).toggleOrderedList().run(),
  },
  {
    title: "To-do",
    description: "Checklist with checkboxes",
    icon: CheckSquare,
    keywords: ["todo", "task", "check", "checklist"],
    action: (editor, range) =>
      editor.chain().focus().deleteRange(range).toggleTaskList().run(),
  },
  {
    title: "Quote",
    description: "Block quote",
    icon: Quote,
    keywords: ["quote", "blockquote"],
    action: (editor, range) =>
      editor.chain().focus().deleteRange(range).setBlockquote().run(),
  },
  {
    title: "Code block",
    description: "Syntax-highlighted code",
    icon: Code,
    keywords: ["code", "codeblock", "pre"],
    action: (editor, range) =>
      editor.chain().focus().deleteRange(range).toggleCodeBlock().run(),
  },
  {
    title: "Divider",
    description: "Horizontal rule",
    icon: Minus,
    keywords: ["hr", "divider", "rule", "line"],
    action: (editor, range) =>
      editor.chain().focus().deleteRange(range).setHorizontalRule().run(),
  },
];

// --- Menu component rendered by ReactRenderer ---
const SlashMenu = forwardRef(({ items, command }, ref) => {
  const [selected, setSelected] = useState(0);

  useEffect(() => setSelected(0), [items]);

  const select = (item) => {
    if (item) command(item);
  };

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (event.key === "ArrowUp") {
        setSelected((i) => (i + items.length - 1) % items.length);
        return true;
      }
      if (event.key === "ArrowDown") {
        setSelected((i) => (i + 1) % items.length);
        return true;
      }
      if (event.key === "Enter") {
        select(items[selected]);
        return true;
      }
      return false;
    },
  }), [items, selected, command]);

  if (items.length === 0) {
    return (
      <div className="bg-white border border-slate-200 shadow-md rounded-sm px-3 py-2 text-xs text-slate-400" data-testid="slash-menu-empty">
        No matching commands
      </div>
    );
  }

  return (
    <div
      className="bg-white border border-slate-200 shadow-md rounded-sm py-1 min-w-[260px] max-h-[300px] overflow-y-auto"
      data-testid="slash-menu"
    >
      {items.map((item, i) => {
        const Icon = item.icon;
        return (
          <button
            key={item.title}
            type="button"
            // Use mousedown + preventDefault so the editor never loses focus
            // mid-selection. This is the established pattern for floating
            // menus that interact with an editor (also used by tiptap's
            // BubbleMenu / FloatingMenu internals).
            onMouseDown={(e) => {
              e.preventDefault();
              select(item);
            }}
            onMouseEnter={() => setSelected(i)}
            className={`w-full flex items-start gap-2.5 px-2.5 py-1.5 text-left text-xs ${
              i === selected ? "bg-slate-100" : "hover:bg-slate-50"
            }`}
            data-testid={`slash-item-${item.title.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <span className="mt-0.5 inline-flex items-center justify-center w-7 h-7 rounded-sm border border-slate-200 bg-white text-slate-600">
              <Icon size={13} />
            </span>
            <span className="flex-1 min-w-0">
              <span className="block font-medium text-[#0a2540]">{item.title}</span>
              <span className="block text-[10px] text-slate-400 truncate">{item.description}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
});

SlashMenu.displayName = "SlashMenu";

// --- TipTap Extension wrapping the Suggestion utility ---
export const SlashCommand = Extension.create({
  name: "slashCommand",

  addOptions() {
    return {
      suggestion: {
        char: "/",
        startOfLine: false,
        allowSpaces: false,
        items: ({ query }) => {
          const q = (query || "").toLowerCase().trim();
          if (!q) return SLASH_ITEMS;
          // Prefix match against title or any keyword (avoids '/he' matching 'cHEcklist')
          return SLASH_ITEMS.filter((it) =>
            it.title.toLowerCase().startsWith(q) ||
            (it.keywords || []).some((k) => k.startsWith(q))
          );
        },
        // `props` here is the item selected from the menu — we invoke its
        // action directly. Any failure is surfaced to the console so we
        // never silently drop commands.
        command: ({ editor, range, props }) => {
          try {
            const ok = props?.action?.(editor, range);
            if (ok === false) {
              // Chain refused (e.g. unknown range). Best effort: clear the slash
              // so the user isn't left with a stray "/" in the body.
              editor.chain().focus().deleteRange(range).run();
            }
          } catch (err) {
            // Diagnostic — should never fire under normal use, but keeps a
            // breadcrumb if the editor's extension wiring ever drifts.
            // eslint-disable-next-line no-console
            console.error("[SlashCommand] item execution failed", err);
            editor.chain().focus().deleteRange(range).run();
          }
        },
        render: () => {
          let component;
          let popup;

          return {
            onStart: (props) => {
              component = new ReactRenderer(SlashMenu, {
                props,
                editor: props.editor,
              });
              if (!props.clientRect) return;
              popup = tippy("body", {
                getReferenceClientRect: props.clientRect,
                appendTo: () => document.body,
                content: component.element,
                showOnCreate: true,
                interactive: true,
                trigger: "manual",
                placement: "bottom-start",
                offset: [0, 6],
              });
            },
            onUpdate: (props) => {
              component?.updateProps(props);
              if (popup && props.clientRect) {
                popup[0].setProps({ getReferenceClientRect: props.clientRect });
              }
            },
            onKeyDown: (props) => {
              if (props.event.key === "Escape") {
                popup?.[0]?.hide();
                return true;
              }
              return component?.ref?.onKeyDown?.(props);
            },
            onExit: () => {
              popup?.[0]?.destroy();
              component?.destroy();
            },
          };
        },
      },
    };
  },

  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        ...this.options.suggestion,
      }),
    ];
  },
});

export default SlashCommand;
